import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { usePlatformAdmin } from '../../../hooks';
import { useStore } from '../../../store';
import { disposeOrgConnection } from '../../../wsConnection';
import { SUPER_REGION_OPTIONS } from '../../../constants/regions';
import { Modal } from '../../../components/shared';
import { InfrastructureEditor } from '../../../components/profiles/InfrastructureEditor';
import { CapacityScheduleEditor } from '../../../components/profiles/CapacityScheduleEditor';
import type { InfrastructureFormData } from '../../../types/profile.types';
import type { CapacitySchedule } from '@interlucent/organization-protocol';
import styles from '../Admin.module.css';

export const Route = createFileRoute('/admin/pools/')({
  component: AdminRoute,
});

// Get API base URL (convert wss:// to https://)
function getApiBaseUrl(): string {
  const wsUrl = (import.meta as any).env.VITE_API_URL || 'wss://api.dev.interlucent.ai';
  return wsUrl.replace(/^wss:/, 'https:').replace(/^ws:/, 'http:');
}

function regionLabel(code: string): string {
  return SUPER_REGION_OPTIONS.find(r => r.value === code)?.label ?? code;
}

interface AdminOrg {
  id: string;
  name: string;
  orgSlug: string | null;
  planTierId: string | null;
  createdAt: string;
  deactivatedAt: string | null;
}

interface AdminPool {
  id: string;
  name: string;
  type: string;
  region: string;
  settingsJson: string | null;
  capacityScheduleJson: string | null;
  active: boolean;
  createdAt: string;
  projectId: string;
  projectName: string;
  orgId: string;
  orgName: string;
  apps: Array<{ appId: string; appName: string }>;
}

interface AdminProject {
  id: string;
  name: string;
  projectSlug: string | null;
  createdAt: string;
}

type SortKey = 'name' | 'backend' | 'region' | 'instances' | 'status';
type SortDir = 'asc' | 'desc';

function AdminRoute() {
  const { isPlatformAdmin } = usePlatformAdmin();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [orgs, setOrgs] = useState<AdminOrg[]>([]);
  const [pools, setPools] = useState<AdminPool[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pool filters
  const [filterOrgId, setFilterOrgId] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterProjectId, setFilterProjectId] = useState<string>('');
  const [filterAppName, setFilterAppName] = useState<string>('');
  const [filterBackendType, setFilterBackendType] = useState<string>('');

  // Pool form state
  const [formOpen, setFormOpen] = useState(false);
  const [deleteConfirmPool, setDeleteConfirmPool] = useState<AdminPool | null>(null);
  const [formSaving, setFormSaving] = useState(false);
  const [infraDefaults, setInfraDefaults] = useState<unknown>(null);

  // Instance counts (loaded async from PoolDO debug endpoints)
  const [instanceCounts, setInstanceCounts] = useState<Map<string, { workers: number; instances: number } | null>>(new Map());

  // Sort state for the pool list
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'name', dir: 'asc' });
  const handleSort = useCallback((key: SortKey) => {
    setSort(prev => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'instances' ? 'desc' : 'asc' });
  }, []);

  // Bulk reprovision state
  const [selectedPoolIds, setSelectedPoolIds] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<{
    total: number;
    done: number;
    results: Array<{ poolId: string; poolName: string; ok: boolean; error?: string }>;
  } | null>(null);

  // Redirect non-admins
  useEffect(() => {
    if (!isPlatformAdmin) {
      navigate({ to: '/' });
    }
  }, [isPlatformAdmin, navigate]);

  const authHeaders = useCallback(async () => {
    const token = await getToken({ template: 'default' });
    if (!token) throw new Error('Authentication required');
    return { Authorization: `Bearer ${token}` };
  }, [getToken]);

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/organizations`, { headers });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const fetched: AdminOrg[] = (data as any).organizations ?? [];
      fetched.sort((a, b) => a.name.localeCompare(b.name));
      setOrgs(fetched);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch organizations');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  const fetchPools = useCallback(async (orgId?: string) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const url = orgId
        ? `${getApiBaseUrl()}/api/admin/pools?orgId=${encodeURIComponent(orgId)}`
        : `${getApiBaseUrl()}/api/admin/pools`;
      const res = await fetch(url, { headers });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setPools((data as any).pools ?? []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch pools');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  const fetchInfraDefaults = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pool-infra-defaults`, { headers });
      if (res.ok) {
        const data = await res.json();
        setInfraDefaults((data as any).defaults);
      }
    } catch { /* non-critical */ }
  }, [authHeaders]);

  // Fetch pools and orgs on mount / filter change
  useEffect(() => {
    if (!isPlatformAdmin) return;
    fetchPools(filterOrgId || undefined);
    fetchInfraDefaults();
    if (orgs.length === 0) fetchOrgs();
  }, [isPlatformAdmin, fetchOrgs, fetchPools, fetchInfraDefaults, filterOrgId]);

  // Fetch instance counts from PoolDO debug endpoints (background, non-blocking)
  useEffect(() => {
    if (pools.length === 0) return;
    let cancelled = false;

    const fetchCounts = async () => {
      const headers = await authHeaders();
      const results = new Map<string, { workers: number; instances: number } | null>();

      await Promise.allSettled(
        pools.map(async (pool) => {
          try {
            const orgId = pool.type !== 'by_request' ? pool.orgId : 'global';
            const res = await fetch(`${getApiBaseUrl()}/pool/${orgId}:${pool.id}/debug`, { headers });
            if (!res.ok) { results.set(pool.id, null); return; }
            const data = await res.json() as any;
            results.set(pool.id, {
              workers: data.connectedWorkers ?? 0,
              instances: data.registryCounts?.total ?? 0,
            });
          } catch {
            results.set(pool.id, null);
          }
        })
      );

      if (!cancelled) setInstanceCounts(results);
    };

    fetchCounts();
    return () => { cancelled = true; };
  }, [pools, authHeaders]);

  const handleSwitchToOrg = useCallback((orgId: string) => {
    disposeOrgConnection();
    useStore.getState().reset();
    useStore.getState().setAdminOrgOverride(orgId);
    navigate({ to: '/' });
  }, [navigate]);

  const handleCreatePool = useCallback(async (form: PoolFormData) => {
    setFormSaving(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          region: form.region,
          projectId: form.projectId || undefined,
          type: form.poolType || undefined,
          settingsJson: form.settingsJson || undefined,
          capacityScheduleJson: form.capacityScheduleJson || undefined,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      setFormOpen(false);
      fetchPools(filterOrgId || undefined);
    } catch (err: any) {
      setError(err.message || 'Failed to create pool');
    } finally {
      setFormSaving(false);
    }
  }, [authHeaders, fetchPools, filterOrgId]);


  const handleDeletePool = useCallback(async (poolId: string) => {
    setFormSaving(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      setDeleteConfirmPool(null);
      fetchPools(filterOrgId || undefined);
    } catch (err: any) {
      setError(err.message || 'Failed to delete pool');
    } finally {
      setFormSaving(false);
    }
  }, [authHeaders, fetchPools, filterOrgId]);

  const togglePoolSelection = useCallback((poolId: string) => {
    setSelectedPoolIds(prev => {
      const next = new Set(prev);
      if (next.has(poolId)) next.delete(poolId);
      else next.add(poolId);
      return next;
    });
  }, []);

  const handleBulkReapplyScale = useCallback(async () => {
    const selected = pools.filter(p => selectedPoolIds.has(p.id));
    if (!selected.length) return;
    if (!window.confirm(`Re-apply manual overrides for ${selected.length} pool(s)?\n\nThis persists current scale targets so they survive infrastructure refresh.`)) return;

    setBulkAction({ total: selected.length, done: 0, results: [] });
    for (const pool of selected) {
      try {
        const headers = await authHeaders();
        const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${pool.id}/reapply-scale`, {
          method: 'POST',
          headers,
        });
        const data = await res.json().catch(() => ({})) as any;
        const skipped = data.skipped;
        setBulkAction(prev => prev && ({
          ...prev,
          done: prev.done + 1,
          results: [...prev.results, {
            poolId: pool.id,
            poolName: pool.name,
            ok: res.ok,
            error: skipped ? data.reason : (!res.ok ? (data.error || `HTTP ${res.status}`) : undefined),
          }],
        }));
      } catch (err: any) {
        setBulkAction(prev => prev && ({
          ...prev,
          done: prev.done + 1,
          results: [...prev.results, { poolId: pool.id, poolName: pool.name, ok: false, error: err.message || 'Unknown error' }],
        }));
      }
    }
  }, [pools, selectedPoolIds, authHeaders]);

  const handleBulkReprovision = useCallback(async (reset: boolean) => {
    const selected = pools.filter(p => selectedPoolIds.has(p.id));
    if (!selected.length) return;
    const msg = reset
      ? `Reset Infrastructure for ${selected.length} pool(s)?\n\nAll instances will be terminated, manual overrides cleared, and infrastructure rebuilt from defaults.`
      : `Refresh Infrastructure for ${selected.length} pool(s)?\n\nAll instances will be terminated and infrastructure rebuilt. Manual scale overrides will be re-applied.`;
    if (!window.confirm(msg)) return;

    setBulkAction({ total: selected.length, done: 0, results: [] });
    for (const pool of selected) {
      try {
        const headers = await authHeaders();
        const url = `${getApiBaseUrl()}/api/admin/pools/${pool.id}/provision${reset ? '?reset=true' : ''}`;
        const res = await fetch(url, { method: 'POST', headers });
        const data = await res.json().catch(() => ({})) as any;
        setBulkAction(prev => prev && ({
          ...prev,
          done: prev.done + 1,
          results: [...prev.results, { poolId: pool.id, poolName: pool.name, ok: res.ok, error: res.ok ? undefined : (data.error || `HTTP ${res.status}`) }],
        }));
      } catch (err: any) {
        setBulkAction(prev => prev && ({
          ...prev,
          done: prev.done + 1,
          results: [...prev.results, { poolId: pool.id, poolName: pool.name, ok: false, error: err.message || 'Unknown error' }],
        }));
      }
    }
  }, [pools, selectedPoolIds, authHeaders]);

  const getPoolBackendTypes = useCallback((pool: AdminPool): string[] => {
    if (!pool.settingsJson) return [];
    try {
      const settings = JSON.parse(pool.settingsJson);
      const backends = settings?.backends ?? [];
      return [...new Set<string>(backends.map((b: { cloud?: string }) => b.cloud).filter(Boolean))];
    } catch { return []; }
  }, []);

  // Set of active (non-deactivated) org ids — used to hide deactivated orgs
  // and any pools they own from admin views (per product requirement).
  // The Organizations page is the only place deactivated orgs remain visible.
  const activeOrgIds = useMemo(() => {
    const set = new Set<string>();
    for (const o of orgs) {
      if (!o.deactivatedAt) set.add(o.id);
    }
    return set;
  }, [orgs]);

  const activeOrgs = useMemo(() => orgs.filter(o => !o.deactivatedAt), [orgs]);

  // Derived filter options from loaded pools
  const backendTypeOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const p of pools) {
      for (const bt of getPoolBackendTypes(p)) seen.add(bt);
    }
    return Array.from(seen).sort();
  }, [pools, getPoolBackendTypes]);

  const projectOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const p of pools) {
      if (p.projectId && p.projectName && !seen.has(p.projectId)) {
        seen.set(p.projectId, p.projectName);
      }
    }
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [pools]);

  const appOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const p of pools) {
      for (const a of p.apps ?? []) {
        seen.add(a.appName);
      }
    }
    return Array.from(seen).sort();
  }, [pools]);

  // Filtered pools
  const filteredPools = useMemo(() => {
    // Hide pools owned by deactivated orgs. on-demand pools (`by_request`)
    // are platform-managed and have no real owning org, so always include them.
    let result = pools.filter(p => p.type === 'by_request' || activeOrgIds.has(p.orgId));
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.orgName.toLowerCase().includes(q) ||
        p.projectName.toLowerCase().includes(q) ||
        p.region.toLowerCase().includes(q)
      );
    }
    if (filterType) {
      result = result.filter(p => p.type === filterType);
    }
    if (filterProjectId) {
      result = result.filter(p => p.projectId === filterProjectId);
    }
    if (filterAppName) {
      result = result.filter(p => (p.apps ?? []).some(a => a.appName === filterAppName));
    }
    if (filterBackendType) {
      result = result.filter(p => getPoolBackendTypes(p).includes(filterBackendType));
    }
    return result;
  }, [pools, activeOrgIds, search, filterType, filterProjectId, filterAppName, filterBackendType, getPoolBackendTypes]);

  // Sorted view of filteredPools. Unknown instance counts (still loading or
  // failed to fetch) always sort to the bottom regardless of direction so the
  // user isn't surprised by them appearing at the top of an "instances desc".
  const sortedPools = useMemo(() => {
    const arr = [...filteredPools];
    const dir = sort.dir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const tieBreak = a.name.localeCompare(b.name);
      switch (sort.key) {
        case 'name':
          return dir * a.name.localeCompare(b.name);
        case 'backend': {
          const av = getPoolBackendTypes(a).join(',');
          const bv = getPoolBackendTypes(b).join(',');
          return dir * av.localeCompare(bv) || tieBreak;
        }
        case 'region':
          return dir * regionLabel(a.region).localeCompare(regionLabel(b.region)) || tieBreak;
        case 'status':
          // active < inactive in asc
          return dir * ((a.active ? 0 : 1) - (b.active ? 0 : 1)) || tieBreak;
        case 'instances': {
          const ac = instanceCounts.get(a.id);
          const bc = instanceCounts.get(b.id);
          const an = ac ? ac.instances : null;
          const bn = bc ? bc.instances : null;
          if (an === null && bn === null) return tieBreak;
          if (an === null) return 1;
          if (bn === null) return -1;
          return dir * (an - bn) || tieBreak;
        }
        default:
          return 0;
      }
    });
    return arr;
  }, [filteredPools, sort, instanceCounts, getPoolBackendTypes]);

  // Running total of instances across the currently-filtered pools. While
  // background fetches are still in-flight we surface a pending count so the
  // user knows the displayed number is partial.
  const instanceSummary = useMemo(() => {
    let total = 0;
    let pending = 0;
    for (const p of filteredPools) {
      const c = instanceCounts.get(p.id);
      if (c === undefined) pending++;
      else if (c !== null) total += c.instances;
    }
    return { total, pending };
  }, [filteredPools, instanceCounts]);

  if (!isPlatformAdmin) return null;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>Pools</h1>
        <span className={styles.count}>{filteredPools.length} / {pools.length}</span>
      </header>

      <div className={styles.searchBar}>
        <input
          className={styles.searchInput}
          type="text"
          placeholder="Search pools..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className={styles.formSelect}
          value={filterOrgId}
          onChange={e => setFilterOrgId(e.target.value)}
        >
          <option value="">All Organizations</option>
          {activeOrgs.map(o => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
        <select
          className={styles.formSelect}
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
        >
          <option value="">All Types</option>
          <option value="by_request">On-Demand</option>
          <option value="private_pool">Private</option>
          <option value="coop_shared_pool">Shared</option>
        </select>
        <select
          className={styles.formSelect}
          value={filterProjectId}
          onChange={e => setFilterProjectId(e.target.value)}
        >
          <option value="">All Projects</option>
          {projectOptions.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <select
          className={styles.formSelect}
          value={filterAppName}
          onChange={e => setFilterAppName(e.target.value)}
        >
          <option value="">All Applications</option>
          {appOptions.map(name => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
        <select
          className={styles.formSelect}
          value={filterBackendType}
          onChange={e => setFilterBackendType(e.target.value)}
        >
          <option value="">All Backends</option>
          {backendTypeOptions.map(bt => (
            <option key={bt} value={bt}>{bt.toUpperCase()}</option>
          ))}
        </select>
        <Link
          to="/admin/pools/pricing"
          className={styles.actionButton}
        >
          Pricing
        </Link>
        <button
          className={styles.actionButton}
          onClick={() => setFormOpen(true)}
        >
          Create Pool
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <PoolList
        pools={sortedPools}
        loading={loading}
        onSwitchToOrg={handleSwitchToOrg}
        onDelete={pool => setDeleteConfirmPool(pool)}
        selectedPoolIds={selectedPoolIds}
        onToggleSelection={togglePoolSelection}
        instanceCounts={instanceCounts}
        sort={sort}
        onSort={handleSort}
        instanceSummary={instanceSummary}
      />

      {/* Bulk Action Bar */}
      {(selectedPoolIds.size > 0 || bulkAction) && (
        <div className={styles.bulkActionBar}>
          {bulkAction ? (
            <div style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                <span style={{ fontWeight: 'var(--weight-semibold)' as any }}>
                  {bulkAction.done < bulkAction.total
                    ? `Reprovisioning ${bulkAction.done + 1} of ${bulkAction.total}...`
                    : `Reprovision complete: ${bulkAction.results.filter(r => r.ok).length}/${bulkAction.total} succeeded`}
                </span>
                {bulkAction.done === bulkAction.total && (
                  <button
                    className={styles.actionButton}
                    onClick={() => { setBulkAction(null); setSelectedPoolIds(new Set()); }}
                  >
                    Dismiss
                  </button>
                )}
              </div>
              {/* Progress bar */}
              <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden', marginBottom: 'var(--space-2)' }}>
                <div style={{ height: '100%', width: `${(bulkAction.done / bulkAction.total) * 100}%`, background: 'var(--text-primary)', transition: 'width 0.3s ease' }} />
              </div>
              {/* Results */}
              {bulkAction.results.length > 0 && (
                <div style={{ fontSize: 'var(--text-xs)', maxHeight: 120, overflowY: 'auto' }}>
                  {bulkAction.results.map(r => (
                    <div key={r.poolId} style={{ display: 'flex', gap: 'var(--space-2)', padding: '2px 0', color: r.ok ? 'var(--text-secondary)' : 'var(--error)' }}>
                      <span>{r.ok ? '\u2713' : '\u2717'}</span>
                      <span>{r.poolName}</span>
                      {r.error && <span style={{ opacity: 0.7 }}>{r.error}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              <span>{selectedPoolIds.size} pool{selectedPoolIds.size !== 1 ? 's' : ''} selected</span>
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <button className={styles.actionButton} onClick={() => setSelectedPoolIds(new Set())}>
                  Deselect All
                </button>
                <button
                  className={styles.actionButton}
                  onClick={() => {
                    const allIds = new Set(filteredPools.map(p => p.id));
                    setSelectedPoolIds(allIds);
                  }}
                >
                  Select All ({filteredPools.length})
                </button>
                <button className={styles.actionButton} onClick={handleBulkReapplyScale}>
                  Re-apply Overrides ({selectedPoolIds.size})
                </button>
                <button className={styles.applyButton} onClick={() => handleBulkReprovision(false)}>
                  Refresh Infrastructure ({selectedPoolIds.size})
                </button>
                <button className={styles.actionButton} onClick={() => handleBulkReprovision(true)} style={{ borderColor: 'var(--error)', color: 'var(--error)' }}>
                  Reset Infrastructure ({selectedPoolIds.size})
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Create Pool Modal */}
      {formOpen && (
        <PoolFormModal
          pool={null}
          saving={formSaving}
          infraDefaults={infraDefaults}
          orgs={activeOrgs}
          authHeaders={authHeaders}
          onClose={() => setFormOpen(false)}
          onSubmit={handleCreatePool}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirmPool && (
        <Modal
          isOpen={true}
          title="Delete Pool"
          subtitle={`Are you sure you want to delete "${deleteConfirmPool.name}"?`}
          onClose={() => setDeleteConfirmPool(null)}
          size="small"
        >
          <div className={styles.confirmActions}>
            <button
              className={styles.actionButton}
              onClick={() => setDeleteConfirmPool(null)}
              disabled={formSaving}
            >
              Cancel
            </button>
            <button
              className={styles.deleteButton}
              onClick={() => handleDeletePool(deleteConfirmPool.id)}
              disabled={formSaving}
            >
              {formSaving ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Global Pools List
// ---------------------------------------------------------------------------

function PoolList({
  pools,
  loading,
  onSwitchToOrg,
  onDelete,
  selectedPoolIds,
  onToggleSelection,
  instanceCounts,
  sort,
  onSort,
  instanceSummary,
}: {
  pools: AdminPool[];
  loading: boolean;
  onSwitchToOrg: (orgId: string) => void;
  onDelete: (pool: AdminPool) => void;
  selectedPoolIds: Set<string>;
  onToggleSelection: (poolId: string) => void;
  instanceCounts: Map<string, { workers: number; instances: number } | null>;
  sort: { key: SortKey; dir: SortDir };
  onSort: (key: SortKey) => void;
  instanceSummary: { total: number; pending: number };
}) {
  if (loading) {
    return <div className={styles.loading}>Loading pools...</div>;
  }

  if (pools.length === 0) {
    return <div className={styles.empty}>No pools found</div>;
  }

  const arrow = (key: SortKey) => sort.key === key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '';
  const instancesLabel = `Instances (${instanceSummary.total}${instanceSummary.pending > 0 ? '…' : ''})`;

  return (
    <div className={styles.poolGrid}>
      <div className={styles.poolTableHeader}>
        <span />
        <button type="button" className={styles.sortHeader} onClick={() => onSort('name')}>Pool{arrow('name')}</button>
        <button type="button" className={styles.sortHeader} onClick={() => onSort('backend')}>Backend{arrow('backend')}</button>
        <button type="button" className={styles.sortHeader} onClick={() => onSort('region')}>Region{arrow('region')}</button>
        <button type="button" className={styles.sortHeader} onClick={() => onSort('instances')}>{instancesLabel}{arrow('instances')}</button>
        <button type="button" className={styles.sortHeader} onClick={() => onSort('status')}>Status{arrow('status')}</button>
        <span />
      </div>
      {pools.map(pool => {
        const counts = instanceCounts.get(pool.id);
        let backendLabel = '';
        try {
          const backends = JSON.parse(pool.settingsJson || '{}')?.backends ?? [];
          const clouds = [...new Set<string>(backends.map((b: { cloud?: string }) => b.cloud).filter(Boolean))];
          backendLabel = clouds.map(c => c.toUpperCase()).join(', ');
        } catch { /* ignore */ }

        return (
          <div key={pool.id} className={styles.poolRow}>
            <input
              type="checkbox"
              checked={selectedPoolIds.has(pool.id)}
              onChange={() => onToggleSelection(pool.id)}
              style={{ cursor: 'pointer', accentColor: 'var(--text-primary)' }}
            />
            <div className={styles.poolInfo}>
              <Link to="/admin/pools/$poolId" params={{ poolId: pool.id }} className={styles.poolName} style={{ color: 'inherit', display: 'block' }}>
                {pool.name}
              </Link>
              <div className={styles.poolMeta}>
                {pool.type === 'by_request' ? 'On-Demand' : pool.type === 'private_pool' ? 'Private' : 'Shared'}
                {' · '}
                {pool.orgName}
              </div>
            </div>
            <div className={styles.poolCell}>{backendLabel || '—'}</div>
            <div className={styles.poolCell}>{regionLabel(pool.region)}</div>
            <div className={styles.poolCell}>
              {counts === undefined
                ? <span style={{ color: 'var(--text-tertiary)' }}>…</span>
                : counts === null
                  ? <span style={{ color: 'var(--text-tertiary)' }}>—</span>
                  : <span className={`${styles.instanceCount}${counts.instances > 0 ? ` ${styles.hasInstances}` : ''}`}>
                      {counts.instances}
                    </span>
              }
            </div>
            <div className={styles.poolCell}>
              <span className={pool.active ? styles.badgeActive : styles.badgeInactive}>
                {pool.active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className={styles.poolActions}>
              <Link to="/admin/pools/$poolId" params={{ poolId: pool.id }} className={styles.actionButton} style={{ padding: 'var(--space-1) var(--space-2)' }}>
                View
              </Link>
              <button
                className={styles.actionButton}
                onClick={() => onDelete(pool)}
                style={{ padding: 'var(--space-1) var(--space-2)' }}
              >
                Del
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pool Form Modal (Create / Edit)
// ---------------------------------------------------------------------------

interface PoolFormData {
  name: string;
  region: string;
  projectId: string | null;
  poolType: 'by_request' | 'private_pool' | 'coop_shared_pool';
  tenancy: 'single' | 'organization' | 'multi';
  settingsJson: string | null;
  capacityScheduleJson: string | null;
  active: boolean;
}

const DEFAULT_INFRA: InfrastructureFormData = {
  backends: [],
  allocationStrategy: 'balanced',
};

const POOL_TYPE_OPTIONS: { value: PoolFormData['poolType']; label: string }[] = [
  { value: 'by_request', label: 'On-Demand (Global)' },
  { value: 'private_pool', label: 'Private Pool' },
  { value: 'coop_shared_pool', label: 'Shared Pool' },
];

function PoolFormModal({
  pool,
  saving,
  infraDefaults,
  orgs,
  authHeaders,
  onClose,
  onSubmit,
}: {
  pool: AdminPool | null;
  saving: boolean;
  infraDefaults?: unknown;
  orgs: AdminOrg[];
  authHeaders: () => Promise<Record<string, string>>;
  onClose: () => void;
  onSubmit: (data: PoolFormData) => void;
}) {
  const isEdit = !!pool;

  const [name, setName] = useState(pool?.name ?? '');
  const [region, setRegion] = useState(pool?.region ?? 'wnam');
  const [active, setActive] = useState(pool?.active ?? true);

  // Org/project selection (create only)
  const [selectedOrgId, setSelectedOrgId] = useState<string>(pool?.orgId ?? '');
  const [selectedProjectId, setSelectedProjectId] = useState<string>(pool?.projectId ?? '');
  const [poolType, setPoolType] = useState<PoolFormData['poolType']>(
    (pool?.type as PoolFormData['poolType']) ?? 'by_request'
  );
  const [orgProjects, setOrgProjects] = useState<AdminProject[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Tenancy: defaults follow pool type convention. `by_request` is multi-tenant
  // by default because global pools accept workers from any project. Org-owned
  // pools default to single-tenant.
  const [tenancy, setTenancy] = useState<PoolFormData['tenancy']>(
    pool?.settingsJson
      ? (() => {
          try {
            const s = JSON.parse(pool.settingsJson);
            if (s.tenancy === 'multi' || s.tenancy === 'organization' || s.tenancy === 'single') return s.tenancy;
          } catch { /* ignore */ }
          return pool.type === 'by_request' ? 'multi' : 'single';
        })()
      : (poolType === 'by_request' ? 'multi' : 'single')
  );

  // When type changes on create, reset tenancy to that type's default so the
  // operator isn't stuck with a nonsensical combination.
  useEffect(() => {
    if (isEdit) return;
    setTenancy(poolType === 'by_request' ? 'multi' : 'single');
  }, [poolType, isEdit]);

  // Auto-prefill the pool name on create when the relevant inputs change.
  // The user can still override manually; we only overwrite if they haven't
  // typed anything themselves.
  const nameTouchedRef = useRef(false);
  useEffect(() => {
    if (isEdit) return;
    if (nameTouchedRef.current) return;
    const org = orgs.find(o => o.id === selectedOrgId);
    const project = orgProjects.find(p => p.id === selectedProjectId);
    const typeLabel = POOL_TYPE_OPTIONS.find(t => t.value === poolType)?.label ?? 'Pool';
    let generated: string;
    if (poolType === 'by_request') {
      generated = `Global ${typeLabel}`;
    } else if (project && org) {
      generated = `${org.name} / ${project.name} ${typeLabel}`;
    } else if (org) {
      generated = `${org.name} ${typeLabel}`;
    } else {
      generated = typeLabel;
    }
    setName(generated);
  }, [isEdit, poolType, selectedOrgId, selectedProjectId, orgs, orgProjects]);

  // Fetch projects when org changes
  useEffect(() => {
    if (!selectedOrgId) {
      setOrgProjects([]);
      setSelectedProjectId('');
      return;
    }
    let cancelled = false;
    setLoadingProjects(true);
    authHeaders().then(headers =>
      fetch(`${getApiBaseUrl()}/api/admin/organizations/${encodeURIComponent(selectedOrgId)}/projects`, { headers })
    ).then(async res => {
      if (!res.ok || cancelled) return;
      const data = await res.json() as { projects: AdminProject[] };
      if (!cancelled) {
        setOrgProjects(data.projects ?? []);
        // Auto-select first project
        if (data.projects?.length > 0 && !selectedProjectId) {
          setSelectedProjectId(data.projects[0].id);
        }
      }
    }).catch(() => {}).finally(() => {
      if (!cancelled) setLoadingProjects(false);
    });
    return () => { cancelled = true; };
  }, [selectedOrgId, authHeaders]);

  // Parse existing settings JSON
  const existingSettings = useMemo(() => {
    if (!pool?.settingsJson) return null;
    try { return JSON.parse(pool.settingsJson); } catch { return null; }
  }, [pool]);

  // Infrastructure
  const [infrastructure, setInfrastructure] = useState<InfrastructureFormData>(() => {
    if (existingSettings) {
      const backends = (existingSettings.backends ?? []).map((b: any) => ({
        ...b,
        awsSubnets: Array.isArray(b.awsSubnets) ? b.awsSubnets.join(', ') : b.awsSubnets,
        awsSecurityGroups: Array.isArray(b.awsSecurityGroups) ? b.awsSecurityGroups.join(', ') : b.awsSecurityGroups,
        gcpFirewallTags: Array.isArray(b.gcpFirewallTags) ? b.gcpFirewallTags.join(', ') : b.gcpFirewallTags,
      }));
      return {
        backends: backends.length ? backends : DEFAULT_INFRA.backends,
        allocationStrategy: existingSettings.allocationStrategy ?? 'balanced',
      };
    }
    return DEFAULT_INFRA;
  });

  // Scaling config. Defaults kept in sync with $poolId.tsx and the backend
  // fallback in OrgWsDO.ts — the old max=10 silently clamped pools at ~3
  // concurrent users once backfill's 3x headroom kicked in, and buffer=1
  // contradicted min=0 drain-to-zero semantics. See pool defaults review.
  const [scalingMin, setScalingMin] = useState(String(existingSettings?.scaling?.min ?? 0));
  const [scalingMax, setScalingMax] = useState(String(existingSettings?.scaling?.max ?? 100));
  const [scalingBuffer, setScalingBuffer] = useState(String(existingSettings?.scaling?.buffer ?? 0));
  // Orchestrator version
  const [orchestratorVersion, setOrchestratorVersion] = useState<string>(existingSettings?.orchestratorVersion ?? '');
  const [orchestratorReleases, setOrchestratorReleases] = useState<Array<{ version: string; releasedAt: string }>>([]);
  const { getToken } = useAuth();
  useEffect(() => {
    (async () => {
      try {
        const token = await getToken({ template: 'default' });
        const res = await fetch(`${getApiBaseUrl()}/api/admin/orchestrator-releases`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json() as { releases: Array<{ version: string; releasedAt: string }> };
          setOrchestratorReleases(data.releases ?? []);
        }
      } catch { /* ignore */ }
    })();
  }, [getToken]);

  // Strategy config
  const [strategyType, setStrategyType] = useState<'reactive' | 'scheduled-floor' | 'scheduled-strict' | 'predictive'>(
    existingSettings?.strategy?.type ?? 'reactive'
  );
  const [scaleUpThreshold, setScaleUpThreshold] = useState(String(existingSettings?.strategy?.scaleUpThreshold ?? 1));
  const [scaleDownDelay, setScaleDownDelay] = useState(String(existingSettings?.strategy?.scaleDownDelaySeconds ?? 300));
  const [cooldownSeconds, setCooldownSeconds] = useState(String(existingSettings?.strategy?.cooldownSeconds ?? 180));
  const [evaluationPeriods, setEvaluationPeriods] = useState(String(existingSettings?.strategy?.evaluationPeriods ?? 2));
  // Scheduled-specific
  const [preScaleMinutes, setPreScaleMinutes] = useState(String(existingSettings?.strategy?.preScaleMinutes ?? 15));
  // Predictive-specific (IL-136: weekly adaptive histogram).
  const [lookaheadMinutes, setLookaheadMinutes] = useState(String(existingSettings?.strategy?.lookaheadMinutes ?? 4));
  const [predictiveQuantile, setPredictiveQuantile] = useState(String(existingSettings?.predictiveQuantile ?? 0.75));
  const [predictiveTimezone, setPredictiveTimezone] = useState(String(existingSettings?.timezone ?? ''));

  const [capacitySchedule, setCapacitySchedule] = useState<CapacitySchedule | undefined>(() => {
    if (pool?.capacityScheduleJson) {
      try {
        return JSON.parse(pool.capacityScheduleJson);
      } catch { /* ignore */ }
    }
    return undefined;
  });

  const handleSubmit = () => {
    if (!name.trim() || !region) return;

    // For org-owned pools, require project selection
    if (poolType !== 'by_request' && !selectedProjectId) return;

    // Create flow: assume server defaults for everything except tenancy. The
    // operator configures the remaining knobs (backends, strategy, schedule,
    // scaling clamps) on the pool detail page after it's been created. This
    // keeps the "I need a pool" path to three meaningful choices —
    // type, tenancy, and which org/project it belongs to.
    if (!isEdit) {
      const settingsJson = JSON.stringify({ tenancy });
      onSubmit({
        name: name.trim(),
        region,
        projectId: poolType !== 'by_request' ? selectedProjectId : null,
        poolType,
        tenancy,
        settingsJson,
        capacityScheduleJson: null,
        active: true,
      });
      return;
    }

    // Edit flow: full config editor remains. (The pool-detail page is the
    // main place for editing now; this modal is still used from the admin
    // list for quick edits and stays feature-complete.)

    // Convert form strings (comma-separated) to arrays for infrastructure backend format
    const backends = infrastructure.backends.map(b => ({
      ...b,
      awsSubnets: b.awsSubnets ? b.awsSubnets.split(',').map(s => s.trim()).filter(Boolean) : undefined,
      awsSecurityGroups: b.awsSecurityGroups ? b.awsSecurityGroups.split(',').map(s => s.trim()).filter(Boolean) : undefined,
      gcpFirewallTags: b.gcpFirewallTags ? b.gcpFirewallTags.split(',').map(s => s.trim()).filter(Boolean) : undefined,
    }));

    // Build strategy config (only include non-default fields for the selected type)
    const strategy: Record<string, unknown> = { type: strategyType };
    const parseNum = (v: string) => { const n = parseFloat(v); return isNaN(n) ? undefined : n; };
    const parseIntVal = (v: string) => { const n = parseInt(v, 10); return isNaN(n) ? undefined : n; };

    // Common
    if (cooldownSeconds !== '180') strategy.cooldownSeconds = parseIntVal(cooldownSeconds);
    if (evaluationPeriods !== '2') strategy.evaluationPeriods = parseIntVal(evaluationPeriods);

    // Reactive
    if (strategyType === 'reactive') {
      if (scaleUpThreshold !== '1') strategy.scaleUpThreshold = parseIntVal(scaleUpThreshold);
      if (scaleDownDelay !== '300') strategy.scaleDownDelaySeconds = parseIntVal(scaleDownDelay);
    }

    // Scheduled (floor or strict)
    if (strategyType === 'scheduled-floor' || strategyType === 'scheduled-strict') {
      if (preScaleMinutes !== '15') strategy.preScaleMinutes = parseIntVal(preScaleMinutes);
      if (scaleDownDelay !== '300') strategy.scaleDownDelaySeconds = parseIntVal(scaleDownDelay);
    }

    // Predictive (IL-136). Forecast horizon belongs to strategy; quantile +
    // timezone are top-level PoolConfig fields.
    if (strategyType === 'predictive') {
      if (lookaheadMinutes !== '4') strategy.lookaheadMinutes = parseIntVal(lookaheadMinutes);
    }
    const predictivePatch: Record<string, unknown> = {};
    if (strategyType === 'predictive') {
      const q = parseNum(predictiveQuantile);
      if (q !== undefined && q !== 0.75) predictivePatch.predictiveQuantile = q;
      const tz = predictiveTimezone.trim();
      if (tz) predictivePatch.timezone = tz;
    }

    const settingsJson = JSON.stringify({
      backends,
      allocationStrategy: infrastructure.allocationStrategy,
      scaling: {
        min: parseIntVal(scalingMin) ?? 0,
        max: parseIntVal(scalingMax) ?? 100,
        buffer: parseIntVal(scalingBuffer) ?? 0,
      },
      strategy,
      ...predictivePatch,
      tenancy,
      ...(orchestratorVersion ? { orchestratorVersion } : {}),
    });

    onSubmit({
      name: name.trim(),
      region,
      projectId: poolType !== 'by_request' ? selectedProjectId : null,
      poolType,
      tenancy,
      settingsJson,
      capacityScheduleJson: capacitySchedule ? JSON.stringify(capacitySchedule) : null,
      active,
    });
  };

  return (
    <Modal
      isOpen={true}
      title={isEdit ? 'Edit Pool' : 'Create Pool'}
      onClose={onClose}
      size="large"
    >
      <div className={styles.formContent}>
        {!isEdit && (
          <>
            <div className={styles.formRow}>
              <label className={styles.formLabel}>Pool Type</label>
              <select
                className={styles.formSelect}
                value={poolType}
                onChange={e => {
                  const val = e.target.value as PoolFormData['poolType'];
                  setPoolType(val);
                  if (val === 'by_request') {
                    setSelectedOrgId('');
                    setSelectedProjectId('');
                  }
                }}
                disabled={saving}
              >
                {POOL_TYPE_OPTIONS.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            {poolType !== 'by_request' && (
              <>
                <div className={styles.formRow}>
                  <label className={styles.formLabel}>Organization</label>
                  <select
                    className={styles.formSelect}
                    value={selectedOrgId}
                    onChange={e => { setSelectedOrgId(e.target.value); setSelectedProjectId(''); }}
                    disabled={saving}
                  >
                    <option value="">Select organization...</option>
                    {orgs.map(o => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                </div>

                {selectedOrgId && (
                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Project</label>
                    <select
                      className={styles.formSelect}
                      value={selectedProjectId}
                      onChange={e => setSelectedProjectId(e.target.value)}
                      disabled={saving || loadingProjects}
                    >
                      {loadingProjects ? (
                        <option value="">Loading projects...</option>
                      ) : orgProjects.length === 0 ? (
                        <option value="">No projects found</option>
                      ) : (
                        <>
                          <option value="">Select project...</option>
                          {orgProjects.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </>
                      )}
                    </select>
                  </div>
                )}
              </>
            )}

            <div className={styles.formRow}>
              <label className={styles.formLabel}>Tenancy</label>
              <select
                className={styles.formSelect}
                value={tenancy}
                onChange={e => setTenancy(e.target.value as PoolFormData['tenancy'])}
                disabled={saving}
              >
                <option value="single">Single — workers must match pool project</option>
                <option value="organization">Organization — workers from any project in the org</option>
                <option value="multi">Multi — workers from any project accepted</option>
              </select>
              {poolType === 'by_request' && tenancy === 'single' && (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-warning, #b45309)', marginTop: 'var(--space-1)' }}>
                  Global on-demand pools are usually multi-tenant. Single restricts workers to the pool's own project.
                </div>
              )}
            </div>
          </>
        )}

        <div className={styles.formRow}>
          <label className={styles.formLabel}>Pool Name</label>
          <input
            className={styles.searchInput}
            type="text"
            value={name}
            onChange={e => { nameTouchedRef.current = true; setName(e.target.value); }}
            placeholder="e.g. Western North America Pool"
            disabled={saving}
          />
          {!isEdit && (
            <div style={{ fontSize: 'var(--text-xs)', opacity: 0.6, marginTop: 'var(--space-1)' }}>
              Prefilled from the selected org/project. Edit to override.
            </div>
          )}
        </div>

        {isEdit && (
          <div className={styles.formRow}>
            <label className={styles.formLabel}>Super Region</label>
            <select
              className={styles.formSelect}
              value={region}
              onChange={e => setRegion(e.target.value)}
              disabled={saving}
            >
              {SUPER_REGION_OPTIONS.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
        )}

        {isEdit && (
          <div className={styles.formRow}>
            <label className={styles.formLabel}>
              <input
                type="checkbox"
                checked={active}
                onChange={e => setActive(e.target.checked)}
                disabled={saving}
              />
              {' '}Active
            </label>
          </div>
        )}

        {!isEdit && (
          <div style={{ padding: 'var(--space-3)', border: 'var(--border-width) dashed var(--border)', fontSize: 'var(--text-xs)', opacity: 0.7, marginTop: 'var(--space-3)' }}>
            Scaling clamps, demand sources, infrastructure backends, and capacity schedule will use platform defaults.
            You can configure them on the pool detail page after creation.
          </div>
        )}

        {isEdit && (
        <>
        <div className={styles.formSection}>
          <h3 className={styles.formSectionTitle}>Scaling</h3>
          <div className={styles.formRow}>
            <label className={styles.formLabel}>Min / Max / Buffer (session slots)</label>
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <input className={styles.searchInput} style={{ width: 80 }} type="number" min="0" value={scalingMin} onChange={e => setScalingMin(e.target.value)} disabled={saving} placeholder="Min" />
              <input className={styles.searchInput} style={{ width: 80 }} type="number" min="0" value={scalingMax} onChange={e => setScalingMax(e.target.value)} disabled={saving} placeholder="Max" />
              <input className={styles.searchInput} style={{ width: 80 }} type="number" min="0" value={scalingBuffer} onChange={e => setScalingBuffer(e.target.value)} disabled={saving} placeholder="Buffer" />
            </div>
          </div>
          <div className={styles.formRow}>
            <label className={styles.formLabel}>Orchestrator Version</label>
            <select className={styles.formSelect} value={orchestratorVersion} onChange={e => setOrchestratorVersion(e.target.value)} disabled={saving}>
              <option value="">Latest</option>
              {orchestratorReleases.map(r => (
                <option key={r.version} value={r.version}>
                  v{r.version} ({new Date(r.releasedAt).toLocaleDateString()})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.formSection}>
          <h3 className={styles.formSectionTitle}>Strategy</h3>
          <div className={styles.formRow}>
            <label className={styles.formLabel}>Type</label>
            <select className={styles.formSelect} value={strategyType} onChange={e => setStrategyType(e.target.value as typeof strategyType)} disabled={saving}>
              <option value="reactive">Reactive</option>
              <option value="scheduled-floor">Scheduled Floor</option>
              <option value="scheduled-strict">Scheduled Strict</option>
              <option value="predictive">Predictive</option>
            </select>
          </div>

          {/* Common settings */}
          <div className={styles.formRow}>
            <label className={styles.formLabel}>Cooldown (seconds between scaling actions)</label>
            <input className={styles.searchInput} style={{ width: 80 }} type="number" min="0" value={cooldownSeconds} onChange={e => setCooldownSeconds(e.target.value)} disabled={saving} />
          </div>
          <div className={styles.formRow}>
            <label className={styles.formLabel}>Evaluation Periods (consecutive before acting)</label>
            <input className={styles.searchInput} style={{ width: 80 }} type="number" min="1" value={evaluationPeriods} onChange={e => setEvaluationPeriods(e.target.value)} disabled={saving} />
          </div>

          {/* Reactive settings */}
          {strategyType === 'reactive' && (
            <>
              <div className={styles.formRow}>
                <label className={styles.formLabel}>Scale-Up Threshold (unmet jobs to trigger)</label>
                <input className={styles.searchInput} style={{ width: 80 }} type="number" min="1" value={scaleUpThreshold} onChange={e => setScaleUpThreshold(e.target.value)} disabled={saving} />
              </div>
              <div className={styles.formRow}>
                <label className={styles.formLabel}>Scale-Down Delay (seconds idle before scale-down)</label>
                <input className={styles.searchInput} style={{ width: 80 }} type="number" min="0" value={scaleDownDelay} onChange={e => setScaleDownDelay(e.target.value)} disabled={saving} />
              </div>
            </>
          )}

          {/* Scheduled settings */}
          {(strategyType === 'scheduled-floor' || strategyType === 'scheduled-strict') && (
            <>
              <div className={styles.formRow}>
                <label className={styles.formLabel}>Pre-Scale Minutes (before window)</label>
                <input className={styles.searchInput} style={{ width: 80 }} type="number" min="0" value={preScaleMinutes} onChange={e => setPreScaleMinutes(e.target.value)} disabled={saving} />
              </div>
              <div className={styles.formRow}>
                <label className={styles.formLabel}>Scale-Down Delay (seconds idle before scale-down)</label>
                <input className={styles.searchInput} style={{ width: 80 }} type="number" min="0" value={scaleDownDelay} onChange={e => setScaleDownDelay(e.target.value)} disabled={saving} />
              </div>
            </>
          )}

          {/* Predictive settings (IL-136: weekly adaptive histogram). */}
          {strategyType === 'predictive' && (
            <>
              <div className={styles.formRow}>
                <label className={styles.formLabel}>Lookahead (forecast horizon, minutes — match backend warm-up)</label>
                <input className={styles.searchInput} style={{ width: 80 }} type="number" min="1" value={lookaheadMinutes} onChange={e => setLookaheadMinutes(e.target.value)} disabled={saving} />
              </div>
              <div className={styles.formRow}>
                <label className={styles.formLabel}>Forecast Quantile (0.5–0.99; default 0.75 from backtest)</label>
                <input className={styles.searchInput} style={{ width: 80 }} type="number" min="0.5" max="0.99" step="0.05" value={predictiveQuantile} onChange={e => setPredictiveQuantile(e.target.value)} disabled={saving} />
              </div>
              <div className={styles.formRow}>
                <label className={styles.formLabel}>Timezone (IANA; default UTC)</label>
                <input className={styles.searchInput} style={{ width: 200 }} type="text" placeholder="UTC" value={predictiveTimezone} onChange={e => setPredictiveTimezone(e.target.value)} disabled={saving} />
              </div>
            </>
          )}
        </div>

        <div className={styles.formSection}>
          <h3 className={styles.formSectionTitle}>Infrastructure</h3>
          <InfrastructureEditor
            value={infrastructure}
            onChange={setInfrastructure}
            disabled={saving}
            infraDefaults={infraDefaults}
          />
        </div>

        <div className={styles.formSection}>
          <h3 className={styles.formSectionTitle}>Capacity Schedule</h3>
          <CapacityScheduleEditor
            value={capacitySchedule}
            onChange={setCapacitySchedule}
            allowMediated={true}
            disabled={saving}
          />
        </div>
        </>
        )}

        <div className={styles.confirmActions}>
          <button
            className={styles.actionButton}
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className={styles.applyButton}
            onClick={handleSubmit}
            disabled={saving || !name.trim() || (poolType !== 'by_request' && !selectedProjectId)}
          >
            {saving ? 'Saving...' : isEdit ? 'Update Pool' : 'Create Pool'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

